import PageHero from '@/components/PageHero';
import TripleSection from '@/components/TripleSection';
import CTABanner from '@/components/CTABanner';
import { useModal } from '@/lib/ModalContext';
import s from '@/components/home.module.css';

const agentWork = [
  'Reads the entire file before it writes a word',
  'Drafts, then verifies its own citations against primary sources',
  'Watches every deadline on the matter, continuously',
  'Breaks a goal into steps and carries them out in order',
  'Keeps an audit trail of everything it did',
  'Knows what it may not decide — and brings that to you',
];

const sovereignFacts = [
  'Work product drafted, reviewed, and stored on machines the firm owns',
  'Runs fully offline — no internet connection required at all',
  'No third-party processor in the loop — nothing to subpoena from a vendor',
  'Your documents never train someone else’s model',
  'Models fine-tuned on the firm’s own forms, style, and jurisdiction',
  'Delivered as an engagement: hardware, deployment, tuning, handover',
];

export default function AgenticAI() {
  const { setOpen } = useModal();
  return (
    <>
      <PageHero
        eyebrow="Agentic AI"
        title="The difference between a chatbot and a colleague."
        sub="A chatbot answers one prompt and stops. An agent carries multi-step work toward a goal — it reads the file, drafts the motion, checks its own citations, watches the deadlines, and comes back to you when a decision needs a lawyer. That is what runs a practice on AVOWAL."
      />

      <section>
        <div className="container">
          <span className={s.sectionLabel}>In plain terms</span>
          <h2 className={s.sectionTitle}>What an agent actually does.</h2>
          <p className={s.sectionLead}>
            No hype required. An agent is software that holds a goal, plans the steps, executes them,
            and checks its own work. Applied to a legal matter, that looks like this:
          </p>
          <ul className={s.capList}>
            {agentWork.map((c) => (
              <li key={c} className={s.capItem}>{c}</li>
            ))}
          </ul>
        </div>
      </section>

      <TripleSection
        id="architecture"
        label="What runs your practice"
        title="One engine. Your firm's name on it."
        lead="ELARIA is the operations engine underneath. What your clients meet is your firm's own assistant — named by you, configured to your practice."
        cards={[
          { num: '/ ENGINE', title: 'ELARIA underneath', body: 'Intake, drafting, citation verification, deadline tracking, filing prep, audit trails. One operations engine runs every firm on the platform — maintained, verified, and improved centrally.' },
          { num: '/ YOUR NAME', title: 'Your firm’s own assistant', body: 'White-label throughout. Clients meet your firm’s named assistant, on your domain, in your brand. They never see AVOWAL, and legal fees go to your firm directly — AVOWAL charges a flat software fee.' },
          { num: '/ YOUR RULES', title: 'Configured per firm', body: 'Your intake questions. Your document templates. Your workflow rules, tone, and disclosure level. The agent is configured to how your practice already works — not the other way around.' },
        ]}
      />

      <TripleSection
        id="supervision"
        label="The line that never moves"
        title="Agents prepare. Attorneys decide."
        lead="Supervision is not a policy memo here. It is the architecture: every consequential act is gated on the attorney, in code."
        cards={[
          { num: '/ GATE', title: 'Consequential acts are gated', body: 'Nothing is signed, filed, sent to a client, or advised without an attorney commit. The gate is a code path the agent cannot route around — a hard stop, not a guideline.' },
          { num: '/ QUEUE', title: 'The escalation queue is your inbox', body: 'The agent completes what it can and queues what it cannot: the decisions, signatures, and judgment calls that require a licensed attorney. You work the queue; the agent works the file.' },
          { num: '/ TRAIL', title: 'Every act is on the record', body: 'What the agent did, what it proposed, and what the attorney decided — logged, timestamped, and reviewable. Supervision you can demonstrate, not just assert.' },
        ]}
      />

      <section id="sovereign">
        <div className="container">
          <span className={s.sectionLabel}>Sovereign</span>
          <h2 className={s.sectionTitle}>On metal, in your office.</h2>
          <p className={s.sectionLead}>
            For firms that want the engine without the cloud: custom agents and custom-trained models
            running on hardware the firm owns, inside the firm&rsquo;s own walls. Draft and assist without
            work product ever leaving the building.
          </p>
          <ul className={s.capList}>
            {sovereignFacts.map((c) => (
              <li key={c} className={s.capItem}>{c}</li>
            ))}
          </ul>
          <p className={s.sectionLead} style={{ marginTop: '2.5rem' }}>
            What never leaves the building cannot be intercepted in transit, subpoenaed from a vendor,
            or folded into someone else&rsquo;s training data. Confidentiality and privilege obligations
            remain matters of the attorney&rsquo;s professional judgment — a sovereign deployment changes
            where the work lives, not who is responsible for it.
          </p>
          <span className={s.quoteAttr}>Founding-firm program &middot; by engagement</span>
          <div className={s.ctaRow} style={{ marginTop: '2rem' }}>
            <button className={`${s.btn} ${s.btnPrimary}`} onClick={() => setOpen('waitlist')}>
              Talk to us about a sovereign deployment (attorneys)
            </button>
          </div>
        </div>
      </section>

      <TripleSection
        id="deployments"
        label="One engine, two homes"
        title="Cloud platform or sovereign deployment."
        cards={[
          { num: '/ CLOUD', title: 'The platform, live today', body: 'Attorney console, client portals, drafting, deadlines, and the court-record bridge — running now, managed for you, updated continuously.' },
          { num: '/ SOVEREIGN', title: 'The engine, on your hardware', body: 'The same agents on machines the firm owns, tuned to your forms and jurisdiction. Can run with no internet connection at all. Founding-firm program, by engagement.' },
          { num: '/ EITHER WAY', title: 'The line holds', body: 'Same engine, same supervision architecture. Agents prepare; attorneys decide. Every consequential act is gated on the attorney, wherever the engine lives.' },
        ]}
      />

      <CTABanner />
    </>
  );
}
